import { useQueryClient } from "@tanstack/react-query";
import { Col, Row, Space, Typography } from "antd";
import { CInfiniteScroll } from "components";
import { useAppLanguage } from "providers/app-language";
import { AppLanguageTypes } from "providers/app-language/interface";
import { useAppPopup } from "providers/app-popup";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setProperty, setUuid } from "service/auth";
import {
  usePatchNotificationsAdminBells,
  useScrollGetNotifications,
} from "service/notification";
import {
  IMenuNoti,
  INotificationDetails,
  MENUS_NOTI,
} from "service/notification/inteface";
import styled from "styled-components";
import tw from "twin.macro";

const StyleDiv = styled.div<{ $active: boolean }>`
  ${tw`cursor-pointer`}
  background-color: ${({ $active }) => ($active ? "#ecf4ee" : "#ffffff")};
  &:hover,
  &:focus {
    background-color: #ecf4ee;
    border-color: #ecf4ee;
    color: #ffffff !important;
  }
  &:active {
    background-color: #ecf4ee;
    border-color: #ecf4ee;
  }
`;

const NotificationContent = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
}) => {
  const navigate = useNavigate();
  const qClient = useQueryClient();
  const { fireNotification } = useAppPopup();
  const mutate = useScrollGetNotifications({ enabled: open });
  const patch = usePatchNotificationsAdminBells();

  const onClick = (item: INotificationDetails, menu?: IMenuNoti) => {
    setOpen(false);

    patch.mutate(
      { ids: [item.id] },
      {
        onSuccess(data) {
          qClient.invalidateQueries(["get-notification"]);
          onRedirect(item, menu);
        },
        onError(error: any) {
          console.log(error);
          fireNotification({ type: "error", message: error.message });
        },
      }
    );
  };

  const onRedirect = (item: INotificationDetails, menu?: IMenuNoti) => {
    if (item?.notiCategory === "APPLIANCES") {
      if (item.propertyUuid) {
        setProperty(item.property, `lpp-property-${item.property.id}`);
        setUuid(item.property?.uuid || "");
        navigate(
          `/property/${item.property?.id}/work-space/work-space-list/${item.itemId}`
        );
      } else {
        navigate(`/work-space/work-space-list/${item.itemId}`);
      }
    } else if (item?.notiCategory === "APPLIANCES_ASSET") {
      if (item.propertyUuid) {
        setProperty(item.property, `lpp-property-${item.property.id}`);
        setUuid(item.property?.uuid || "");
        navigate(
          `/property/${item.property?.id}/work-space/work-space-list/${item.itemId}`
        );
      } else {
        navigate(`/work-space/work-space-list/${item.itemId}`);
      }
    } else if (item?.notiCategory === "APPLIANCES_SUPERFLUOUS") {
      if (item.propertyUuid) {
        setProperty(item.property, `lpp-property-${item.property.id}`);
        setUuid(item.property?.uuid || "");
        navigate(
          `/property/${item.property?.id}/work-space/work-space-list/superfluous/${item.itemId}`
        );
      } else {
        navigate(`/work-space/work-space-list/superfluous/${item.itemId}`);
      }
    } else if (item.propertyUuid) {
      setProperty(item.property, `lpp-property-${item.property.id}`);
      setUuid(item.property?.uuid || "");
      navigate(`/property/${item.property?.id}${menu?.to}/${item.itemId}`);
    } else {
      navigate(`${menu?.to}/${item.itemId}`);
    }
  };

  return (
    <div>
      <CInfiniteScroll
        mutation={mutate}
        height={"calc(100vh - 20vh)"}
        style={{ overflow: "hidden", overflowY: "auto" }}
        render={(data: INotificationDetails[]) => {
          return (
            <Space direction="vertical" className="!gap-0 w-full">
              {data?.map((item, i) => {
                const menu = MENUS_NOTI.find(
                  (e) => e.notiCategory === item.notiCategory
                );

                return (
                  <StyleDiv key={i} $active={!item.isRead}>
                    <Content
                      item={item}
                      menu={menu}
                      onClick={() => onClick(item, menu)}
                    />
                  </StyleDiv>
                );
              })}
            </Space>
          );
        }}
      />
    </div>
  );
};

const NotificationItem = styled.div`
  svg * {
    ${tw`fill-primary`}
  }
`;

interface IProps {
  item: INotificationDetails;
  onClick?: () => void;
  menu?: IMenuNoti;
}

const Content: FC<IProps> = ({ item, onClick, menu }) => {
  const { formatDate } = useAppLanguage();
  const { i18n, t } = useTranslation();
  const lang = i18n.language as AppLanguageTypes;

  const { notiDetail, createdAt, property, propertyUuid } = item;
  const Icon = menu?.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
  const detail = lang === "th" ? notiDetail.th : notiDetail.en;
  const date = formatDate({ format: "full-month-time", date: createdAt });
  const menuTitle = t(menu?.title ?? "", { ns: "menu" });
  const propertyName = propertyUuid ? property?.nameTh : t("central");

  return (
    <Row className="pr-4 py-2" onClick={onClick}>
      <Col span={4} className="flex justify-center ">
        <NotificationItem>
          {Icon && <Icon height={30} width={30} />}
        </NotificationItem>
      </Col>
      <Col span={20}>
        <div className="text-[#2061E7] text-[12px]">{propertyName}</div>
        <div className="text-[#44955B] text-[12px]">{menuTitle}</div>
        <Typography.Paragraph ellipsis={{ rows: 2 }} className="!m-0">
          <span className="text-[#0A1430] text-[12px]">{detail}</span>
        </Typography.Paragraph>
        <div className="text-[#AFBAC7] text-[10px]">{date}</div>
      </Col>
    </Row>
  );
};

export default NotificationContent;
